import { SagaIterator } from "redux-saga";
import { all, call, fork, put, takeEvery } from "redux-saga/effects";
import i18next from "i18next";

import subscriptionAPIService from "../../services/api/subscription";
import Toaster from "../../services/toaster";
import {
  IRenewalInvoiceStatus,
  ISubscriptionRequest,
} from "../../services/api/subscription/types";
import { Invoice } from "../../types/common";
import { getMonthDayYearDate, parseUtc } from "../../utils/common";
import {
  CREATE_RENEWAL_INVOICE,
  FETCH_RENEWAL_INVOICE_STATUS,
  RENEWAL_NOT_OPEN,
  SHOWABLE_ERROR_STATUSES,
} from "./constants";
import {
  createRenewalInvoiceError,
  createRenewalInvoiceSuccess,
  fetchRenewalInvoiceStatus,
  fetchRenewalInvoiceStatusError,
  fetchRenewalInvoiceStatusSuccess,
} from "./actions";
import {
  ICreateRenewalInvoiceAction,
  IFetchRenewalInvoiceStatusAction,
} from "./types";

// ph-api answers with RFC 7807 problem+json; only 409 and 502 carry customer-readable messages.
export const getRenewalErrorMessage = (error: any): string | undefined => {
  const response = error?.response;

  if (!SHOWABLE_ERROR_STATUSES.includes(response?.status)) {
    return undefined;
  }

  const data = response?.data;
  const recovery = data?.recovery;

  // Renewal window not yet open; show when it opens.
  if (recovery?.code === RENEWAL_NOT_OPEN && recovery?.payable_from) {
    const payableFrom = parseUtc(recovery.payable_from);

    if (!isNaN(Number(payableFrom))) {
      return i18next.t("renewalPaymentNotOpenYet", {
        date: getMonthDayYearDate(payableFrom),
      });
    }
  }

  return data?.detail || undefined;
};

export function* fetchRenewalInvoiceStatusSaga({
  payload,
}: IFetchRenewalInvoiceStatusAction): SagaIterator {
  try {
    const status: IRenewalInvoiceStatus = yield call(
      [subscriptionAPIService, subscriptionAPIService.getRenewalInvoiceStatus],
      payload as ISubscriptionRequest,
    );

    yield put(
      fetchRenewalInvoiceStatusSuccess({
        subscriptionId: payload.subscriptionId,
        status,
      }),
    );
  } catch (error) {
    // No toast; logged for support. Banner renders retry.
    // eslint-disable-next-line no-console
    console.error(
      `Failed to fetch the renewal status of subscription ${payload.subscriptionId}`,
      error,
    );

    yield put(
      fetchRenewalInvoiceStatusError({
        subscriptionId: payload.subscriptionId,
        message: getRenewalErrorMessage(error),
      }),
    );
  }
}

export function* createRenewalInvoiceSaga({
  payload,
}: ICreateRenewalInvoiceAction): SagaIterator {
  try {
    const invoice: Invoice = yield call(
      [subscriptionAPIService, subscriptionAPIService.createRenewalInvoice],
      payload as ISubscriptionRequest,
    );

    yield put(
      createRenewalInvoiceSuccess({
        subscriptionId: payload.subscriptionId,
        invoice,
      }),
    );

    if (invoice?.is_open && invoice.sp_customer_invoice_uri) {
      // Open asynchronously; popup blocker may block.
      const paymentWindow = window.open(
        invoice.sp_customer_invoice_uri,
        "_blank",
      );

      if (paymentWindow) {
        paymentWindow.opener = null;
      } else {
        Toaster.warning({ translationKey: "renewalPaymentPopupBlocked" });
      }
    } else if (invoice && !invoice.is_open) {
      Toaster.success({ translationKey: "renewalPaymentSettled" });
    } else {
      Toaster.error({ translationKey: "renewalPaymentError" });
    }

    // Refresh status.
    yield put(fetchRenewalInvoiceStatus(payload));
  } catch (error) {
    const message = getRenewalErrorMessage(error);

    yield put(
      createRenewalInvoiceError({
        subscriptionId: payload.subscriptionId,
        message,
      }),
    );

    if (message) {
      Toaster.error({ content: message });
    } else {
      Toaster.error({ translationKey: "renewalPaymentError" });
    }

    // Re-read status.
    yield put(fetchRenewalInvoiceStatus(payload));
  }
}

export function* watchFetchRenewalInvoiceStatus(): SagaIterator {
  // takeEvery: handle multiple cards fetching independently.
  yield takeEvery(FETCH_RENEWAL_INVOICE_STATUS, fetchRenewalInvoiceStatusSaga);
}

export function* watchCreateRenewalInvoice(): SagaIterator {
  yield takeEvery(CREATE_RENEWAL_INVOICE, createRenewalInvoiceSaga);
}

export function* renewalInvoiceSaga(): SagaIterator {
  yield all([
    fork(watchFetchRenewalInvoiceStatus),
    fork(watchCreateRenewalInvoice),
  ]);
}
